import { Box, Button, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { SnackSimple } from '../../../../../../components'
import { useContextState } from '../../../../../../hooks/theme'
import { icons } from '../../../../../../utils/icons'
import { Status, Types } from '../../../../../../utils/enums'
import { SnackbarFormat } from '../../../../../../utils/types/generals'
import { CustomPaper } from '../../../../../../components/CustomPaper'
import TimerRemainingComponent from '../../components/timer'
import { downloadReportUrl } from './methods'
import { LeftProps } from './type'
import { useStyles } from './styles'
import dayjs from 'dayjs'
import { Constants } from '../../../../../../utils/constants'

function Left({
  mode,
  status,
  userCreator,
  userAssigned,
  store,
  approverUsers,
  typeActionPlan,
  endDate,
  startDate,
  createDate,
  stoper,
  cancelLoading,
  reportUrl,
  showCancelActionPlan,
  showReassignButton,
  openReassign,
  cancelActionPlan,
  canAnswer,
}: LeftProps) {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const styles = useStyles()
  const [snackbar, setSnackbar] = useState<SnackbarFormat>()
  const [isDownloading, setIsDownloading] = useState(false)

  const isManual = !['automatic', 'automatic_later'].includes(typeActionPlan?.toLowerCase() ?? '')
  const isAutomaticMode = !isManual
  const showTimer = mode === 'items-questions' || mode === 'items-single-question'
  const approversToShow = isAutomaticMode && userAssigned && !approverUsers?.length
    ? [{ label: userAssigned.label, value: userAssigned.label, id: 'assigned', email: userAssigned.email }]
    : approverUsers

  const downloadReport = useCallback(async () => {
    setIsDownloading(true)
    const { response } = await downloadReportUrl({ url: reportUrl, id })
    setIsDownloading(false)
    if (response) {
      setSnackbar({
        open: true,
        message: t('actionPlan.successDownloadReport'),
        type: Types.SUCCESS,
      })
      return
    }
    setSnackbar({
      open: true,
      message: t('actionPlan.errorDownloadReport'),
      type: Types.ERROR,
    })
  }, [reportUrl, id, t])

  const closeSnackbar = () => {
    setSnackbar(undefined)
  }

  return (
    <Box sx={styles.container} className="h-full">
      {showTimer && (
        <Box sx={styles.block}>
          <Typography sx={styles.blockTitle}>{t('actionPlan.tags.timeRemaining')}</Typography>
          <TimerRemainingComponent stoper={stoper} limitDate={endDate} requestDate={startDate} />
        </Box>
      )}

      <Box sx={styles.block}>
        <Typography sx={styles.blockTitle}>
          {isManual ? t('actionPlan.solutionDateLabel') : t('actionPlan.requestDate')}
        </Typography>

        <Box sx={styles.dateItem}>
          <Typography sx={{...styles.approverItemName, fontWeight: 'normal', padding: '4px 0', margin: 'auto'}}>
            {isManual
              ? dayjs(endDate, Constants.dayjsFormat).locale('es').format(Constants.dayjsHumanFormat)
              : (createDate ? dayjs(createDate).locale('es').format(Constants.dayjsHumanFormat) : 'N/A')}
          </Typography>
        </Box>
      </Box>

      {!!store && (
        <Box sx={styles.block}>
          <Typography sx={styles.blockTitle}>{t('actionPlan.referencedStore')}</Typography>

          <Box sx={styles.approverItem}>
            <Box sx={styles.approverIcon}>{icons('store')}</Box>
            <Box sx={styles.approverItemText}>
              <Typography sx={styles.approverItemName}>{store.label}</Typography>

              {!!store.footer && (
                <Typography sx={styles.approverItemEmail}>{store.footer}</Typography>
              )}

              {!!store.timeZone && (
                <Typography sx={styles.approverItemEmail}>
                  {t('general.timeZoneDetail', { timeZone: store.timeZone })}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {!!userCreator && (
        <Box sx={styles.block}>
          <Typography sx={styles.blockTitle}>{t('actionPlan.tags.assigningUser')}</Typography>

          <Box sx={styles.approverItem}>
            <Box sx={styles.approverIcon}>{icons('personFilled')}</Box>
            <Box sx={styles.approverItemText}>
              <Typography sx={styles.approverItemName}>{userCreator.label}</Typography>
              {!!userCreator.date && (
                <Typography sx={styles.approverItemEmail}>
                  {dayjs(userCreator.date).locale('es').format(Constants.dayjsHumanFormat)}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {!!userAssigned && (
        <Box sx={styles.block}>
          <Typography sx={styles.blockTitle}>{t('actionPlan.tags.assignedUser')}</Typography>

          <Box sx={styles.approverItem}>
            <Box sx={styles.approverIcon}>{icons('personFilled')}</Box>
            <Box sx={styles.approverItemText}>
              <Typography sx={styles.approverItemName}>{userAssigned.label}</Typography>
              {!!userAssigned.date && (
                <Typography sx={styles.approverItemEmail}>
                  {dayjs(userAssigned.date).locale('es').format(Constants.dayjsHumanFormat)}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={styles.block}>
        <Typography sx={styles.blockTitle}>{t('actionPlan.approversTitle')}</Typography>

        <CustomPaper sx={styles.approversContainer}>
          {!!approversToShow?.length && (
            <Box sx={styles.approversList}>
              {approversToShow.map((approver, index) => (
                <Box key={index} sx={styles.approverItem}>
                  <Box sx={styles.approverIcon}>{icons('personFilled')}</Box>
                  <Box sx={styles.approverItemText}>
                    <Typography sx={styles.approverItemName}>{approver.label}</Typography>
                    {!!approver.email && (
                      <Typography sx={styles.approverItemEmail}>{approver.email}</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {!approversToShow?.length && (
            <Typography sx={styles.noData}>{t('actionPlan.noAprovers')}</Typography>
          )}
        </CustomPaper>
      </Box>

      <Box sx={styles.actionsContainer}>
        {!!canAnswer && status?.value === Status.AWAIT_SOLUTION && (
          <Button
            fullWidth
            variant='contained'
            onClick={() => {
              navigate(`/dashboard/action-plan/created/${id}/solve`)
            }}
            sx={{ borderRadius: '4px', fontWeight: 'bold' }}
          >
            {t('actionPlan.solve')}
          </Button>
        )}

        {!!showReassignButton && (
          <Button fullWidth onClick={openReassign} variant='dark_c_nr'>
            {t('actionPlan.reassignActionPlan')}
          </Button>
        )}

        <Button
          fullWidth
          variant='outlined'
          sx={styles.downloadButton}
          onClick={downloadReport}
          disabled={isDownloading}
          startIcon={icons('download')}
        >
          {t('general.downloadReport')}
        </Button>

        {!!showCancelActionPlan && (
          <Button
            fullWidth
            variant='outlined'
            sx={styles.cancelButton}
            onClick={() => {
              if (!cancelLoading) {
                cancelActionPlan()
              }
            }}
            disabled={cancelLoading}
          >
            {t('general.cancelActionPlan')}
          </Button>
        )}
      </Box>

      <SnackSimple close={closeSnackbar} open={snackbar?.open ?? false} type={snackbar?.type ?? Types.SUCCESS} message={snackbar?.message ?? ''} />
    </Box>
  )
}

export default Left
